import React, { useState } from "react";
import { Mail, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

/**
 * Shared "Email me my results" card used at the bottom of every calculator.
 * Captures the email as a soft lead (stored in MongoDB).
 * Email delivery itself is pending Resend/SendGrid integration.
 */
export default function EmailResultsCard({
  calculator,
  payload,
  label,
  description,
  labelKey,
  descKey,
}) {
  const { t } = useLang();
  const resolvedLabel = labelKey ? t(labelKey, label) : (label || t("email.label.default"));
  const resolvedDesc = descKey ? t(descKey, description) : (description || t("email.desc.default"));
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading" || status === "success") return;
    if (!email || !email.includes("@")) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      // Public lead-capture endpoint — explicitly disable credentials so the
      // browser doesn't enforce the strict "Allow-Origin must not be *" rule
      // that fires when withCredentials is true AND the response has Allow-Origin: *.
      // (That was producing silent CORS-blocked failures on production calc.nztaxation.nz → tradie-calc.emergent.host.)
      await api.post(
        "/leads/email-results",
        {
          email: email.trim().toLowerCase(),
          calculator,
          payload,
          consent_marketing: consent,
        },
        { withCredentials: false },
      );
      setStatus("success");
    } catch (err) {
      setStatus("error");
      // Friendlier error decode: network/CORS failure has no `response`,
      // server errors do.
      const detail = err?.response?.data?.detail;
      const isNetwork = !err?.response;
      setErrorMsg(
        detail ||
          (isNetwork
            ? "Network blocked — check your connection or try again in a moment."
            : "Couldn't save your email right now. Please try again in a moment."),
      );
    }
  };

  if (status === "success") {
    return (
      <div
        className="mt-8 border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-5 sm:p-6"
        data-testid={`email-results-success-${calculator}`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2
            size={22}
            className="text-[var(--primary)] shrink-0 mt-0.5"
          />
          <div className="min-w-0">
            <div className="font-display text-lg">{t("email.success_title", "Sent! Check your inbox.")}</div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
              {t("email.success_body", "We've fired off your calculator snapshot to your inbox. The first email from us might land in your Spam or Promotions folder — drag it into your inbox once and Gmail/Yahoo will learn to deliver future ones straight there.")}
            </p>
            <p className="text-[11px] font-mono text-zinc-500 mt-3 uppercase tracking-wider leading-relaxed">
              {t("email.success_tip", "Tip · don't see it? Check Spam / Junk / Promotions, then mark as 'Not Spam'.")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-8 border border-[var(--border)] p-5 sm:p-6 bg-[var(--surface)]"
      data-testid={`email-results-card-${calculator}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-[var(--primary)]/10 border border-[var(--primary)]/30 flex items-center justify-center shrink-0">
          <Mail size={18} className="text-[var(--primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-mono">{t("email.eyebrow", "Save this calc")}</div>
          <h3 className="font-display text-xl tracking-tight mt-1">{resolvedLabel}</h3>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{resolvedDesc}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-4">
        <input
          type="email"
          required
          placeholder="you@yourtradiebusiness.co.nz"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input-sharp py-3 text-sm"
          data-testid={`email-results-input-${calculator}`}
          autoComplete="email"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`email-results-submit-${calculator}`}
        >
          {status === "loading" ? (
            t("email.button_loading", "Sending…")
          ) : (
            <>
              <Send size={14} /> {t("email.button", "Email it")}
            </>
          )}
        </button>
      </div>

      <label className="flex items-start gap-2 mt-3 cursor-pointer text-[11px] font-mono text-zinc-500 leading-relaxed">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[var(--primary)]"
          data-testid={`email-results-consent-${calculator}`}
        />
        <span>
          {t("email.consent", "Send me the occasional NZ tax tip from NZ Taxation Ltd. No spam, ever — unsubscribe one-click.")}
        </span>
      </label>

      {status === "error" && (
        <div
          className="mt-3 flex items-start gap-2 text-xs text-[var(--error)]"
          data-testid={`email-results-error-${calculator}`}
        >
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}
    </form>
  );
}
